import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { MinAccessLevelGuard } from './guards/min-access-level.guard';
import { UserAccessLevel } from './services/user.service';
import { AppFeatures } from './app.features';

import { FeedResolver } from './feed.resolver';
import { UserResolver } from '../admin/user.resolver';
import { DashboardResolver } from './dashboard.resolver';
import { WorkspaceResolver } from './workspace.resolver';
import { AuthentificationResolver } from './authetification.resolver';

import { HomeComponent } from './containers/home/home.component';
import { FeedComponent } from './containers/feed/feed.component';
import { FeedsComponent } from './containers/feeds/feeds.component';
import { AlertsComponent } from './containers/alerts/alerts.component';
import { AnalyticsComponent } from './containers/analytics/analytics.component';
import { DashboardComponent } from './containers/dashboard/dashboard.component';
import { DashboardsComponent } from './containers/dashboards/dashboards.component';
import { DimensionsComponent } from './containers/dimensions/dimensions.component';
import { DashboardPageComponent } from './containers/dashboard/dashboard-page.component';
import { UserProfileComponent } from './components/user-profile/user-profile.component';
import { WorkspaceComponent } from './containers/workspace/workspace/workspace.component';
import { AlertsCenterComponent } from './containers/alerts-center/alerts-center.component';
import { NotificationsComponent } from './containers/notifications/notifications.component';
import { WelcomeScreenComponent } from './components/welcome-screen/welcome-screen.component';
import { ExportSchedulesGridComponent } from '../shared/components/export-schedules-grid/export-schedules-grid.component';
import { AutoStoryRunsComponent } from './containers/auto-story-runs/auto-story-runs.component';
import { ContextualNotFoundComponent } from '../shared/components/contextual-not-found/contextual-not-found.component';

const routes: Routes = [
  {
    path: '',
    resolve: {
      user: AuthentificationResolver,
    },
    data: {
      section: 'analytics',
    },
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'analytics',
      },
      {
        path: 'analytics',
        // Authentication for the whole analytics tree (the former per-child
        // canLoad entries never ran: canLoad applies to lazy modules only).
        // Sections hidden from the menu re-assert their level or feature below.
        canActivate: [MinAccessLevelGuard],
        component: AnalyticsComponent,
        data: {
          minAccessLevel: UserAccessLevel.GUEST,
          breadcrumb: 'breadcrumb.anlytics.analytics',
          breadcrumbIcon: ['fas', 'chart-line'],
          pageTitle: 'page-title.anlytics.analytics',
        },
        children: [
          {
            path: '',
            redirectTo: 'home',
            pathMatch: 'full',
          },
          {
            path: 'welcome',
            component: WelcomeScreenComponent,
          },
          {
            path: 'home',
            component: HomeComponent,
            data: {
              // breadcrumb: 'breadcrumb.anlytics.home',
              // breadcrumbIcon: ['fas', 'home'],
            },
          },
          {
            path: 'user/:userId',
            component: UserProfileComponent,
            resolve: {
              user: UserResolver,
            },
          },
          {
            path: 'connectors',
            canActivate: [MinAccessLevelGuard],
            loadChildren: () =>
              import('../connector-manager/connector-manager.module').then(
                (m) => m.ConnectorManagerModule,
              ),
            data: {
              minAccessLevel: UserAccessLevel.CLIENT_ADMIN,
              breadcrumb: 'breadcrumb.analytics.connectors',
              breadcrumbIcon: ['fas', 'plug'],
              breadcrumbContext: 'connectors',
              pageTitle: 'page-title.analytics.connectors',
              // Company-scoped mount; the Admin module mounts the same lazy
              // module cross-company (WAP-1629, connector-manager.scope.ts).
              connectorManagerScope: 'analytics',
            },
          },
          {
            path: 'feeds',
            component: FeedsComponent,

            data: {
              breadcrumb: 'breadcrumb.analytics.feeds',
              breadcrumbIcon: ['fas', 'rss'],
              breadcrumbContext: 'feeds',
              pageTitle: 'page-title.analytics.feeds',
            },
          },
          {
            path: 'dimensions',
            component: DimensionsComponent,

            data: {
              breadcrumb: 'breadcrumb.analytics.dimensions',
              breadcrumbIcon: ['fas', 'rss'],
              breadcrumbContext: 'dimensions',
              pageTitle: 'page-title.analytics.dimensions',
            },
          },
          {
            path: 'dashboards',
            component: DashboardsComponent,

            data: {
              breadcrumbContext: 'dashboards',
              breadcrumb: 'breadcrumb.analytics.dashboards',
              breadcrumbIcon: ['fas', 'tachometer-alt'],
              pageTitle: 'page-title.analytics.dashboards',
            },
          },
          {
            path: 'alerts',
            component: AlertsComponent,

            data: {
              breadcrumbContext: 'alerts',
              breadcrumb: 'breadcrumb.analytics.alerts',
              breadcrumbIcon: ['fas', 'bell'],
              pageTitle: 'page-title.analytics.alerts',
            },
          },
          {
            path: 'home/:tab',
            component: HomeComponent,
            data: {},
          },
          {
            path: 'schedules',
            component: ExportSchedulesGridComponent,
            data: {
              schedulesScope: 'my',
              breadcrumbContext: 'schedules',
              breadcrumb: 'breadcrumb.analytics.schedules',
              breadcrumbIcon: ['fas', 'calendar-days'],
              pageTitle: 'page-title.analytics.schedules',
            },
          },
          {
            path: 'auto-story-runs',
            canActivate: [MinAccessLevelGuard],
            component: AutoStoryRunsComponent,
            data: {
              minAccessLevel: UserAccessLevel.CLIENT_USER,
              requiredFeature:
                AppFeatures.ADVANCED_MASS_OPERATIONS_AI_AUTO_STORY,
              breadcrumbContext: 'auto-story-runs',
              breadcrumb: 'breadcrumb.analytics.auto-story-runs',
              breadcrumbIcon: ['fas', 'wand-magic-sparkles'],
              pageTitle: 'page-title.analytics.auto-story-runs',
            },
          },
          {
            path: 'notifications',
            component: NotificationsComponent,
            data: {
              breadcrumb: 'breadcrumb.notifications.my-notifications',
              breadcrumbIcon: ['fas', 'bell'],
              pageTitle: 'page-title.analytics.my-notifications',
            },
          },
          {
            path: 'alerts-center',
            component: AlertsCenterComponent,
            data: {
              breadcrumb: 'breadcrumb.notifications.my-alerts',
              breadcrumbIcon: ['fas', 'bell'],
              pageTitle: 'page-title.analytics.my-alerts',
            },
          },
          {
            path: 'workspace/:workspaceId',
            component: WorkspaceComponent,
            data: { breadcrumbContext: 'workspace' },
            resolve: {
              workspace: WorkspaceResolver,
            },
          },
          {
            path: 'workspace/:workspaceId/:tab',
            component: WorkspaceComponent,
            data: { breadcrumbContext: 'workspace' },

            resolve: {
              workspace: WorkspaceResolver,
            },
          },
          {
            path: 'workspace/:workspaceId/feed/:feedId',
            component: FeedComponent,
            data: {},
            resolve: {
              feed: FeedResolver,
              workspace: WorkspaceResolver,
            },
          },
          {
            path: 'workspace/:workspaceId/dashboard/:dashboardId',
            component: DashboardComponent,
            data: {},
            resolve: {
              workspace: WorkspaceResolver,
              dashboard: DashboardResolver,
            },
            children: [
              {
                path: 'page/:pageId',
                component: DashboardPageComponent,
              },
              {
                path: '',
                component: DashboardPageComponent,
              },
            ],
          },
          {
            path: 'workspace/:workspaceId/dashboard/:dashboardId/feed/:feedId',
            component: FeedComponent,
            data: {},
            resolve: {
              workspace: WorkspaceResolver,
              dashboard: DashboardResolver,
              feed: FeedResolver,
            },
          },

          { path: '**', component: ContextualNotFoundComponent },
        ],
      },
    ],
  },
];
@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class CoreRoutingModule {}
